import type {
  AssistantAction, BatchProposalParams, GhostFeature, ItemLayer, LayerName, SelectableType,
} from './assistantTypes';

const DEFAULT_PROPOSAL_COLOR = '#ffcc00';

/**
 * Everything applyAction is allowed to touch. Keeping this narrow is what lets the whole
 * client-side semantics of the assistant be tested with a plain object and no React.
 */
export interface ActionTarget {
  flyTo: (lng: number, lat: number, zoom: number) => void;
  fitBounds: (bbox: number[], padding: number) => void;
  selectItem: (type: SelectableType, id: number) => void;
  setLayerVisibility: (layer: LayerName, visible: boolean, itemIds: number[] | null) => void;
  setMapStyle: (style: string) => void;
  setDrawMode: (mode: string) => void;
  setGhosts: (ghosts: GhostFeature[]) => void;
}

/** Turn a batch proposal into ghost previews, honouring which features are still ticked. */
export function ghostsForBatch(
  params: BatchProposalParams, selected?: string[] | null,
): GhostFeature[] {
  const ticked = selected ? new Set(selected) : null;
  return params.features.map((feature) => ({
    proposalId: params.proposal_id,
    featureId: feature.feature_id,
    kind: feature.kind,
    name: feature.name,
    color: feature.color || DEFAULT_PROPOSAL_COLOR,
    coordinates: feature.coordinates,
    metrics: feature.metrics,
    selected: ticked ? ticked.has(feature.feature_id) : true,
  }));
}

export const ITEM_LAYERS: ItemLayer[] = ['points', 'polygons', 'lines', 'sensors', 'units'];

/** Apply one action envelope. Unknown actions are ignored rather than thrown — a newer backend
 *  must never break an older tab. */
export function applyAction(action: AssistantAction, target: ActionTarget): void {
  switch (action.action) {
    case 'fly_to':
      target.flyTo(action.params.lng, action.params.lat, action.params.zoom);
      break;

    case 'fit_bounds':
      target.fitBounds(action.params.bbox, action.params.padding);
      break;

    case 'select_item':
      target.selectItem(action.params.type, action.params.id);
      break;

    case 'set_layer_visibility':
      target.setLayerVisibility(
        action.params.layer, action.params.visible, action.params.item_ids ?? null);
      break;

    case 'set_map_style':
      target.setMapStyle(action.params.style);
      break;

    case 'set_draw_mode':
      target.setDrawMode(action.params.mode);
      break;

    case 'propose_feature': {
      const p = action.params;
      target.setGhosts([{
        proposalId: p.proposal_id,
        kind: p.kind,
        name: p.name,
        color: p.color || DEFAULT_PROPOSAL_COLOR,
        coordinates: p.coordinates,
        metrics: p.metrics,
        selected: true,
      }]);
      // Frame the proposal so the user can see what they are being asked to confirm.
      if (p.metrics?.bbox?.length === 4) {
        target.fitBounds(p.metrics.bbox, 80);
      }
      break;
    }

    case 'propose_features': {
      const p = action.params;
      if (!p.features?.length) break;
      target.setGhosts(ghostsForBatch(p, null));
      if (p.bbox?.length === 4) {
        target.fitBounds(p.bbox, 80);
      }
      break;
    }

    // A model offer changes nothing on the map — it renders a button in the chat bubble, and the
    // panel owns that. Listed explicitly so it is clear this is deliberate rather than missed.
    case 'propose_model_activation':
      break;

    default:
      break;
  }
}

export function applyActions(actions: AssistantAction[], target: ActionTarget): void {
  actions.forEach((action) => applyAction(action, target));
}

/** Human-readable summary of a metrics block, used in the proposal bubble. */
export function describeMetrics(kind: string, metrics?: { area_ha?: number; length_m?: number; vertex_count?: number }): string {
  if (!metrics) return '';
  if (kind === 'polygon' && metrics.area_ha !== undefined) {
    return metrics.area_ha >= 1
      ? `${metrics.area_ha.toFixed(1)} ha`
      : `${Math.round(metrics.area_ha * 10000)} m²`;
  }
  if (kind === 'line' && metrics.length_m !== undefined) {
    return metrics.length_m >= 1000
      ? `${(metrics.length_m / 1000).toFixed(2)} km`
      : `${Math.round(metrics.length_m)} m`;
  }
  return '';
}
